import { CdkDrag, DragDropModule } from '@angular/cdk/drag-drop';
import { Component, computed, inject, input, output } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { LocalizationService } from '../../../core/localization/localization.service';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import {
  FORM_FIELD_CATEGORY_ORDER,
  FORM_FIELD_TYPES,
  FormFieldCategory,
  FormFieldType,
  FormFieldTypeCatalogItem
} from '../../../shared/dynamic-form/form-schema.models';

@Component({
  standalone: true,
  selector: 'ain-form-builder-palette',
  imports: [DragDropModule, ButtonModule, InfoHintComponent],
  template: `
    <aside class="palette card">
      <div class="header">
        <h3>{{ t('dynamic.builder.palette.title') }}</h3>
        <ain-info-hint
          [titleKey]="'dynamic.builder.palette.infoTitle'"
          [descriptionKey]="'dynamic.builder.palette.infoDescription'"
          [bulletKeys]="['dynamic.builder.palette.infoBullet']" />
      </div>

      @for (category of categories; track category) {
        <section class="category">
          <h4>{{ categoryLabel(category) }}</h4>
          <div class="field-list">
            @for (fieldType of fieldsByCategory()[category]; track fieldType.value) {
              <button
                type="button"
                class="palette-item"
                cdkDrag
                [cdkDragData]="fieldType.value"
                [cdkDragDisabled]="disabled()"
                (click)="addField.emit(fieldType.value)">
                <i [class]="fieldType.icon"></i>
                <span>{{ t(fieldType.labelKey) }}</span>
              </button>
            }
          </div>
        </section>
      }
    </aside>
  `,
  styles: [`
    .palette { display: grid; gap: 12px; align-content: start; }
    .header { display: grid; gap: 8px; }
    .header h3 { margin: 0; }
    .category h4 {
      margin: 0 0 8px;
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #64748b;
    }
    .field-list { display: grid; gap: 6px; }
    .palette-item {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 8px 10px;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      background: #f8fafc;
      cursor: grab;
      text-align: start;
    }
    .palette-item:hover { border-color: #0f766e; background: #ecfdf5; }
    .palette-item i { color: #0f766e; }
    .card {
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 12px;
      background: #fff;
    }
  `]
})
export class FormBuilderPaletteComponent {
  private readonly localization = inject(LocalizationService);

  readonly disabled = input(false);
  readonly addField = output<FormFieldType>();

  readonly categories = FORM_FIELD_CATEGORY_ORDER;

  readonly fieldsByCategory = computed(() => {
    const grouped = {} as Record<FormFieldCategory, FormFieldTypeCatalogItem[]>;
    for (const category of FORM_FIELD_CATEGORY_ORDER) {
      grouped[category] = FORM_FIELD_TYPES.filter(item => item.category === category);
    }
    return grouped;
  });

  categoryLabel(category: FormFieldCategory): string {
    return this.t(`dynamic.builder.palette.category.${category}`);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
